(() => {
  'use strict';

  const CONFIG = {
    openFirstSection: true,
    closeOtherSections: true,
    introductorySectionTitle: 'Opis produktu',

    productScopeSelectors: [
      '[data-section-type="main-product"]',
      '[data-product-information]',
      '.product-information',
      '.product-info',
      'main'
    ],

    descriptionSelectors: [
      '[data-product-description]',
      '.product__description',
      '.product-description',
      '.product__description-content',
      '.product-info__description',
      '.product-details__description',
      '.product-description__content',
      'rte-formatter.rte',
      '.rte'
    ]
  };

  function isProductPage() {
    return window.location.pathname.includes('/products/');
  }

  function cleanTitle(text) {
    let title = text
      .replace(/\s+/g, ' ')
      .replace(/[:：]\s*$/, '')
      .trim();

    /*
     * Przykład:
     * "REPTI PLANET Nawilżacz Ręczny 1,5l - Dane Techniczne"
     * zmienia na:
     * "Dane Techniczne"
     */
    const parts = title.split(/\s+-\s+/);

    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1].trim();

      if (lastPart.length <= 50) {
        title = lastPart;
      }
    }

    return title;
  }

  function getHeadingTitle(element) {
    if (!(element instanceof HTMLElement)) {
      return null;
    }

    if (['H2', 'H3'].includes(element.tagName)) {
      return cleanTitle(element.textContent);
    }

    /*
     * Rozpoznaje:
     * <p><strong>Skład</strong></p>
     * <p><b>Dawkowanie</b></p>
     */
    if (element.tagName === 'P') {
      const children = Array.from(element.children);

      if (
        children.length === 1 &&
        ['STRONG', 'B'].includes(children[0].tagName) &&
        element.textContent.trim() === children[0].textContent.trim()
      ) {
        return cleanTitle(children[0].textContent);
      }
    }

    return null;
  }

  function getDirectContentRoot(candidate) {
    /*
     * Clarity może umieszczać treść bezpośrednio w rte-formatter
     * albo w pojedynczym wewnętrznym kontenerze.
     */
    if (candidate.children.length === 1) {
      const onlyChild = candidate.firstElementChild;

      if (
        onlyChild &&
        ['DIV', 'SECTION'].includes(onlyChild.tagName) &&
        onlyChild.children.length > 1
      ) {
        return onlyChild;
      }
    }

    return candidate;
  }

  function countRecognizedHeadings(container) {
    return Array.from(container.children).filter((element) => {
      return Boolean(getHeadingTitle(element));
    }).length;
  }

  function findDescriptionContainer(root = document) {
    for (const scopeSelector of CONFIG.productScopeSelectors) {
      const scopes = root.querySelectorAll(scopeSelector);

      for (const scope of scopes) {
        for (const selector of CONFIG.descriptionSelectors) {
          const candidates = scope.querySelectorAll(selector);

          for (const candidate of candidates) {
            if (candidate.dataset.zooplicityAccordionProcessed) {
              continue;
            }

            const contentRoot = getDirectContentRoot(candidate);
            const headingsCount = countRecognizedHeadings(contentRoot);
            const textLength = contentRoot.textContent.trim().length;

            /*
             * Opisy z nagłówkami dzielimy na kilka sekcji.
             * Opisy bez nagłówków zamieniamy na jedną sekcję
             * „Opis produktu”.
             */
            const hasDescriptionContent = Boolean(
              contentRoot.querySelector('p, ul, ol, table')
            );

            if (
              textLength >= 80 &&
              hasDescriptionContent &&
              (headingsCount >= 1 || textLength >= 120)
            ) {
              return {
                container: candidate,
                contentRoot
              };
            }
          }
        }
      }
    }

    return null;
  }

  function hasMeaningfulContent(nodes) {
    const temporaryContainer = document.createElement('div');

    nodes.forEach((node) => {
      temporaryContainer.appendChild(node.cloneNode(true));
    });

    return (
      temporaryContainer.textContent.replace(/\s+/g, ' ').trim().length > 0 ||
      Boolean(
        temporaryContainer.querySelector(
          'img, video, iframe, table, ul, ol'
        )
      )
    );
  }

  function parseSections(contentRoot) {
    const nodes = Array.from(contentRoot.childNodes);
    const sections = [];

    let currentTitle = CONFIG.introductorySectionTitle;
    let currentNodes = [];
    let foundHeading = false;

    function saveSection() {
      if (hasMeaningfulContent(currentNodes)) {
        sections.push({
          title: currentTitle,
          nodes: currentNodes
        });
      }

      currentNodes = [];
    }

    nodes.forEach((node) => {
      const headingTitle =
        node.nodeType === Node.ELEMENT_NODE
          ? getHeadingTitle(node)
          : null;

      if (headingTitle) {
        saveSection();
        currentTitle = headingTitle;
        foundHeading = true;
        return;
      }

      currentNodes.push(node);
    });

    saveSection();

    return {
      foundHeading,
      sections
    };
  }

  function createAccordionItem(title, nodes, index) {
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    const titleElement = document.createElement('span');
    const icon = document.createElement('span');
    const content = document.createElement('div');

    details.className = 'zooplicity-product-accordion__item';

    summary.className = 'zooplicity-product-accordion__summary';
    summary.setAttribute(
      'aria-controls',
      `zooplicity-accordion-panel-${index}`
    );

    titleElement.className = 'zooplicity-product-accordion__title';
    titleElement.textContent = title;

    icon.className = 'zooplicity-product-accordion__icon';
    icon.setAttribute('aria-hidden', 'true');

    content.className = 'zooplicity-product-accordion__content';
    content.id = `zooplicity-accordion-panel-${index}`;

    nodes.forEach((node) => {
      content.appendChild(node);
    });

    summary.append(titleElement, icon);
    details.append(summary, content);

    return details;
  }

  function enableSingleOpenMode(accordion) {
    if (!CONFIG.closeOtherSections) {
      return;
    }

    const items = accordion.querySelectorAll(
      '.zooplicity-product-accordion__item'
    );

    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) {
          return;
        }

        items.forEach((otherItem) => {
          if (otherItem !== item) {
            otherItem.open = false;
          }
        });
      });
    });
  }

  function transformDescription(result) {
    if (!result) {
      return;
    }

    const { container, contentRoot } = result;
    const { foundHeading, sections } = parseSections(contentRoot);

    if (sections.length < 1) {
      return;
    }

    const accordion = document.createElement('div');

    accordion.className = 'zooplicity-product-accordion';
    accordion.setAttribute('data-zooplicity-product-accordion', '');

    sections.forEach((section, index) => {
      const item = createAccordionItem(
        section.title,
        section.nodes,
        index + 1
      );

      if (CONFIG.openFirstSection && index === 0) {
        item.open = true;
      }

      accordion.appendChild(item);
    });

    enableSingleOpenMode(accordion);

    contentRoot.replaceChildren(accordion);

    container.dataset.zooplicityAccordionProcessed = 'true';
    container.classList.add(
      'zooplicity-product-description--accordion'
    );
  }

  function initialize(root = document) {
    if (!isProductPage()) {
      return;
    }

    transformDescription(findDescriptionContainer(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();

/* ============================================================
   ZOOPLICITY PDP DESKTOP FULL-WIDTH ACCORDION V1
   Desktop >= 990px:
   - opis/accordion opuszcza prawą kolumnę product-details,
   - staje się trzecim elementem .product-information__grid,
   - zajmuje obie kolumny pod galerią + modułem zakupowym.
   Mobile pozostaje w oryginalnym miejscu.
   ============================================================ */
(() => {
  if (!/\/products\//.test(window.location.pathname)) return;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  let scheduled = false;

  function restoreMobile(productGrid) {
    const wide = productGrid.querySelector(
      ':scope > .zooplicity-product-description--accordion[data-zoo-desktop-wide="true"]'
    );
    if (!wide) return;

    const productInfo = productGrid.closest('[data-testid="product-information"]');
    const origin = productInfo?.querySelector('[data-zoo-accordion-origin="true"]');

    if (origin?.parentNode) {
      origin.replaceWith(wide);
      wide.removeAttribute('data-zoo-desktop-wide');
    }
  }

  function applyDesktop(productGrid) {
    const productInfo = productGrid.closest('[data-testid="product-information"]');
    if (!productInfo) return;

    const existingWide = productGrid.querySelector(
      ':scope > .zooplicity-product-description--accordion[data-zoo-desktop-wide="true"]'
    );

    const source = productInfo.querySelector(
      '.product-details .zooplicity-product-description--accordion'
    );

    if (source) {
      if (existingWide && existingWide !== source) {
        existingWide.remove();
      }

      let origin = productInfo.querySelector('[data-zoo-accordion-origin="true"]');
      if (origin) origin.remove();

      origin = document.createElement('span');
      origin.hidden = true;
      origin.setAttribute('data-zoo-accordion-origin', 'true');
      source.before(origin);

      source.setAttribute('data-zoo-desktop-wide', 'true');
      productGrid.append(source);
    }
  }

  function sync() {
    scheduled = false;

    document
      .querySelectorAll('[data-testid="product-information"] .product-information__grid')
      .forEach((productGrid) => {
        if (desktopQuery.matches) {
          applyDesktop(productGrid);
        } else {
          restoreMobile(productGrid);
        }
      });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sync);
  }

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (typeof desktopQuery.addEventListener === 'function') {
    desktopQuery.addEventListener('change', schedule);
  } else if (typeof desktopQuery.addListener === 'function') {
    desktopQuery.addListener(schedule);
  }

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);

  schedule();
})();
/* /ZOOPLICITY PDP DESKTOP FULL-WIDTH ACCORDION V1 */
